import React, { useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DNAbsenceReasonGraph = ({
  issues,
  colorMapping,
  onItemSelect = null
}) => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  const chartRef = useRef(null);

  const uniqueReasons = useMemo(() => {
    return [...new Set(issues.map(issue => issue.reason || 'Non catégorisé'))];
  }, [issues]);

  const chartData = useMemo(() => {
    if (!issues.length || !colorMapping) return null;

    // Fonction helper pour parser correctement les dates
    const parseDate = (dateString) => {
      try {
        // Safari ne supporte pas bien les formats "YYYY-MM-DD HH:MM:SS.SSS +ZZZZ"
        // Convertissons-le en format compatible
        if (typeof dateString === 'string') {
          // Extraction des composants de la date
          // Supprimer les tirets au début s'il y en a
          const cleanDateString = dateString.replace(/^[\s–-]+/, '');

          // Extraire la partie de la date sans le fuseau horaire
          const dateParts = cleanDateString.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/) || [];

          if (dateParts.length >= 7) {
            // Créer une date avec les composants extraits
            const [_, year, month, day, hours, minutes, seconds] = dateParts;
            return new Date(year, month - 1, day, hours, minutes, seconds);
          }

          // Essayons d'autres formats potentiels
          const date = new Date(cleanDateString);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }

        // Si c'est déjà un objet Date
        if (dateString instanceof Date && !isNaN(dateString.getTime())) {
          return dateString;
        }

        return null;
      } catch (e) {
        console.error("Erreur lors du parsing de la date:", e, dateString);
        return null;
      }
    };

    // Déterminer si on doit regrouper par semaine ou par jour
    const shouldGroupByWeek = (() => {
      // Extraire toutes les dates valides
      const validDates = issues
        .map(issue => parseDate(issue.date))
        .filter(date => date !== null);

      if (validDates.length < 2) return false;

      // Trouver la date min et max
      const minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
      const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));

      // Calculer la différence en jours
      const diffTime = Math.abs(maxDate - minDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Si la plage dépasse 14 jours, grouper par semaine
      return diffDays > 14;
    })();

    // Grouper par jour ou par semaine selon la plage de dates
    const groupedData = issues.reduce((acc, issue) => {
      try {
        // Utiliser notre fonction de parsing sécurisée
        const issueDate = parseDate(issue.date);

        if (!issueDate) {
          console.warn("Date invalide rencontrée:", issue.date);
          return acc; // Ignorer cette entrée
        }

        let groupKey;

        if (shouldGroupByWeek) {
          // Grouper par semaine
          const weekStart = new Date(issueDate);
          // Obtenir le jour de la semaine (0 = dimanche, 1 = lundi, etc.)
          const dayOfWeek = weekStart.getDay();
          // Ajuster pour commencer la semaine le lundi (en France)
          const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          weekStart.setDate(weekStart.getDate() - diff);
          // Réinitialiser l'heure à minuit
          weekStart.setHours(0, 0, 0, 0);

          // Format YYYY-MM-DD pour la clé
          groupKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
        } else {
          // Grouper par jour
          groupKey = `${issueDate.getFullYear()}-${String(issueDate.getMonth() + 1).padStart(2, '0')}-${String(issueDate.getDate()).padStart(2, '0')}`;
        }

        const reason = issue.reason || 'Non catégorisé';

        if (!acc[groupKey]) {
          acc[groupKey] = {};
        }
        if (!acc[groupKey][reason]) {
          acc[groupKey][reason] = 0;
        }
        acc[groupKey][reason]++;
        return acc;
      } catch (error) {
        console.error("Erreur lors du traitement de la date:", error, issue);
        return acc; // Continuer même en cas d'erreur
      }
    }, {});

    // Si aucune donnée n'a été correctement traitée, sortir
    if (Object.keys(groupedData).length === 0) {
      console.warn("Aucune donnée valide trouvée après le traitement des dates");
      return null;
    }

    // Obtenir toutes les périodes triées
    const sortedPeriods = Object.keys(groupedData).sort();

    // Format des dates pour l'affichage
    const formattedLabels = sortedPeriods.map(period => {
      try {
        const [year, month, day] = period.split('-').map(Number);
        if (shouldGroupByWeek) {
          // Pour les semaines, afficher "Sem. du JJ/MM"
          return `Sem. du ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
        } else {
          // Pour les jours, afficher JJ/MM
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
        }
      } catch (e) {
        return period; // Fallback si le format n'est pas comme attendu
      }
    });

    // Créer les datasets pour toutes les raisons
    const datasets = uniqueReasons.map(reason => ({
      label: reason,
      data: sortedPeriods.map(period => groupedData[period][reason] || 0),
      borderColor: colorMapping[reason]?.background,
      backgroundColor: colorMapping[reason]?.backgroundWithOpacity,
      fill: true,
      tension: 0.4,
      hidden: false
    }));

    return {
      labels: formattedLabels,
      datasets
    };
  }, [issues, colorMapping, uniqueReasons]);

  const handleElementClick = (reason) => {
    if (onItemSelect && typeof onItemSelect === 'function') {
      // Trouver les statistiques pour cette raison
      const weeklyData = {};
      issues.forEach(issue => {
        if ((issue.reason || 'Non catégorisé') === reason) {
          weeklyData[reason] = (weeklyData[reason] || 0) + 1;
        }
      });

      onItemSelect({ reason, count: weeklyData[reason] || 0 }, reason);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: true
    },
    scales: {
      x: {
        grid: {
          color: isDarkMode ? '#374151' : '#E5E7EB',
        },
        ticks: {
          color: isDarkMode ? '#E5E7EB' : '#374151',
        }
      },
      y: {
        stacked: true,
        grid: {
          color: isDarkMode ? '#374151' : '#E5E7EB',
        },
        ticks: {
          color: isDarkMode ? '#E5E7EB' : '#374151',
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
        onClick: (evt, legendItem) => {
          handleElementClick(legendItem.text);
        },
        labels: {
          color: isDarkMode ? '#E5E7EB' : '#374151',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        mode: 'index'
      }
    },
    onClick: (evt, elements) => {
      if (elements.length > 0) {
        const datasetIndex = elements[0].datasetIndex;
        const reason = chartData.datasets[datasetIndex].label;
        handleElementClick(reason);
      }
    }
  };

  if (!chartData) return null;

  return (
    <div className="space-y-2">
      <div className="h-[300px] relative">
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default DNAbsenceReasonGraph;